import * as Sentry from "@sentry/react";
import axios, { type AxiosResponse } from "axios";

import type {
  DatafeedConfiguration,
  IBasicDataFeed,
  LibrarySymbolInfo,
  SearchSymbolResultItem,
} from "~/chart";
import instance from "~/v2/api";
import { MustReauthenticateError } from "~/v2/api/auth/MustReauthenticateError";
import { getAssetsViaFullTextSearch } from "~/v2/api/rest/assets";
import type {
  TradingViewBar,
  TradingViewDatafeedConfig,
} from "./TradingViewChart.types";
import {
  DAILY_RESOLUTION,
  HISTORY_BAR_LIMIT,
  HISTORY_MIN_WINDOW_MS,
  MAX_CONSECUTIVE_EMPTY_WINDOWS,
  MAX_CONSECUTIVE_EMPTY_WINDOWS_OVERNIGHT,
  MAX_CONSECUTIVE_POLL_ERRORS,
  MIN_EMPTY_WINDOW_STEP_BACK_SEC,
  MIN_POLL_LOOKBACK_MS,
  OVERNIGHT_HISTORY_MIN_WINDOW_MS,
  TRADING_VIEW_POLLING_INTERVALS,
  TRADING_VIEW_SUPPORTED_RESOLUTIONS,
} from "./tradingView.constants";
import {
  deduplicateBars,
  formatBars,
  formatChartHistoryEndDate,
  resolutionToTimeframe,
} from "./tradingView.utils";

// Stop the poll loop on an auth failure. A dead session throws
// MustReauthenticateError from the request interceptor; a raw data-feed 401 is
// no longer wrapped (it does not opt into logoutOn401), so match it explicitly
// to avoid hammering the endpoint after it starts returning 401.
const isUnauthorizedPollError = (err: unknown): boolean =>
  err instanceof MustReauthenticateError ||
  (axios.isAxiosError(err) && err.response?.status === 401);

const fulfilledResponses = (
  results: PromiseSettledResult<AxiosResponse>[],
): PromiseFulfilledResult<AxiosResponse>[] =>
  results.filter(
    (result): result is PromiseFulfilledResult<AxiosResponse> =>
      result.status === "fulfilled",
  );

const rejectionReasons = (
  results: PromiseSettledResult<AxiosResponse>[],
): unknown[] =>
  results
    .filter(
      (result): result is PromiseRejectedResult => result.status === "rejected",
    )
    .map((result) => result.reason);

const barsFromResponses = (
  responses: PromiseFulfilledResult<AxiosResponse>[],
): TradingViewBar[] =>
  responses.flatMap((response) => formatBars(response.value.data?.bars));

/**
 * Full port of the v2 `TVChartContainer.createDataFeed` as a pure factory.
 * `dispose` replaces the component's `clearAllPolling` unmount hook.
 */
export const createTradingViewDatafeed = (
  config: TradingViewDatafeedConfig,
): { datafeed: IBasicDataFeed; dispose: () => void } => {
  const {
    baseUrl,
    crypto,
    cryptoDataApiUrl,
    exchange,
    isUnlimited,
    isOvernightTradable,
  } = config;

  // Fresh per datafeed, and copied again per `resolveSymbol` call, so a
  // charting-library mutation can never leak across symbols or charts.
  const datafeedConfiguration: DatafeedConfiguration = {
    supported_resolutions: [...TRADING_VIEW_SUPPORTED_RESOLUTIONS],
  };

  const pollIntervals = new Map<string, ReturnType<typeof setInterval>>();

  const stopPolling = (listenerGuid: string): void => {
    const intervalId = pollIntervals.get(listenerGuid);
    if (intervalId) {
      clearInterval(intervalId);
      pollIntervals.delete(listenerGuid);
    }
  };

  // Overnight-tradable stocks use the standard extended session (4am-8pm)
  // plus a separate overnight session (8pm-4am). TradingView's "0400-0400"
  // format caused the current trading day to not render, so we use an
  // explicit two-segment session instead.
  const session = crypto
    ? "24x7"
    : isOvernightTradable
      ? "0400-2000,2000-0400"
      : "0400-2000";

  const onReady: IBasicDataFeed["onReady"] = (cb) => {
    setTimeout(() => cb(datafeedConfiguration), 0);
  };

  const resolveSymbol: IBasicDataFeed["resolveSymbol"] = (
    symbolName,
    onSymbolResolvedCallback,
  ) => {
    const symbolInfo: LibrarySymbolInfo = {
      name: symbolName,
      description: "",
      type: "stock",
      session,
      timezone: "America/New_York",
      ticker: symbolName,
      minmov: 1,
      pricescale: 100,
      has_intraday: true,
      intraday_multipliers: ["1", "15", "30", "60"],
      supported_resolutions: [...TRADING_VIEW_SUPPORTED_RESOLUTIONS],
      volume_precision: 8,
      data_status: "streaming",
      exchange,
      listed_exchange: exchange,
      format: "price",
      subsession_id: crypto ? undefined : "extended",
      subsessions: crypto
        ? undefined
        : [
            {
              description: "Regular Trading Hours",
              id: "regular",
              session: "0930-1600",
            },
            {
              description: "Extended Trading Hours",
              id: "extended",
              session,
            },
            {
              description: "Pre-market",
              id: "premarket",
              session: "0400-0930",
            },
            {
              description: "Post-market",
              id: "postmarket",
              session: isOvernightTradable
                ? "1600-2000,2000-0400"
                : "1600-2000",
            },
          ],
    };

    setTimeout(() => onSymbolResolvedCallback(symbolInfo), 0);
  };

  const searchSymbols: IBasicDataFeed["searchSymbols"] = (
    userInput,
    _exchange,
    _symbolType,
    onResult,
  ) => {
    const query = userInput.trim();
    if (!query) {
      onResult([]);
      return;
    }

    void getAssetsViaFullTextSearch(query).then(
      ({ assets }) => {
        const results: SearchSymbolResultItem[] = assets
          .filter(
            (asset) => asset.class === "us_equity" || asset.class === "crypto",
          )
          .map((asset) => ({
            symbol: asset.symbol,
            ticker: asset.symbol,
            description: asset.name,
            exchange: asset.exchange,
            type: asset.class === "crypto" ? "crypto" : "stock",
          }));
        onResult(results);
      },
      (error: unknown) => {
        Sentry.captureException(error, {
          tags: { component: "tv-chart-symbol-search" },
          extra: { query },
        });
        onResult([]);
      },
    );
  };

  const subscribeBars: IBasicDataFeed["subscribeBars"] = (
    symbolInfo,
    resolution,
    onTick,
    listenerGuid,
  ) => {
    const timeframe = resolutionToTimeframe(resolution);
    const pollMs = TRADING_VIEW_POLLING_INTERVALS[resolution];
    if (!timeframe || !pollMs) {
      return;
    }
    if (!isUnlimited && !crypto) {
      return;
    }

    const pollTags = { component: "tv-chart-poll", symbol: symbolInfo.name };
    let consecutivePollErrors = 0;

    const handlePollFailure = (
      reasons: unknown[],
      capture: (reasons: unknown[]) => void,
    ): void => {
      if (reasons.some(isUnauthorizedPollError)) {
        stopPolling(listenerGuid);
        return;
      }
      consecutivePollErrors++;
      if (consecutivePollErrors >= MAX_CONSECUTIVE_POLL_ERRORS) {
        capture(reasons);
      }
    };

    const pollCrypto = (start: string, end: string): void => {
      const url = `${cryptoDataApiUrl}/bars?symbols=${symbolInfo.name}&start=${start}&end=${end}&timeframe=${timeframe}&limit=1&sort=desc`;
      instance
        .get(url)
        .then(({ data }) => {
          consecutivePollErrors = 0;
          const bars = formatBars(data?.bars?.[symbolInfo.name]);
          if (bars.length > 0) {
            onTick(bars[0]);
          }
        })
        .catch((err) => {
          handlePollFailure([err], () => {
            Sentry.captureException(err, { tags: pollTags });
          });
        });
    };

    const pollStocks = (start: string, end: string): void => {
      const buildUrl = (feed?: string) =>
        `${baseUrl}/stocks/${symbolInfo.name}/bars?start=${start}&end=${end}&timeframe=${timeframe}&adjustment=split&limit=1&sort=desc${
          feed ? `&feed=${feed}` : ""
        }`;

      const requests: Promise<AxiosResponse>[] = [instance.get(buildUrl())];
      if (isOvernightTradable) {
        requests.push(instance.get(buildUrl("boats")));
      }

      Promise.allSettled(requests).then((results) => {
        const fulfilled = fulfilledResponses(results);

        if (fulfilled.length === 0) {
          handlePollFailure(rejectionReasons(results), (reasons) => {
            Sentry.captureException(
              new Error("Stock polling failed for all data sources"),
              { tags: pollTags, extra: { reasons } },
            );
          });
          return;
        }

        consecutivePollErrors = 0;
        const bars = barsFromResponses(fulfilled).sort(
          (a, b) => b.time - a.time,
        );
        if (bars.length > 0) {
          onTick(bars[0]);
        }
      });
    };

    const poll = () => {
      const end = new Date().toISOString();
      const lookbackMs = Math.max(pollMs * 3, MIN_POLL_LOOKBACK_MS);
      const start = new Date(Date.now() - lookbackMs).toISOString();

      if (crypto) {
        pollCrypto(start, end);
        return;
      }
      pollStocks(start, end);
    };

    stopPolling(listenerGuid);
    pollIntervals.set(listenerGuid, setInterval(poll, pollMs));
    poll();
  };

  const unsubscribeBars: IBasicDataFeed["unsubscribeBars"] = (listenerGuid) => {
    stopPolling(listenerGuid);
  };

  let consecutiveEmpty = 0;
  let lastResolution = "";

  const getBars: IBasicDataFeed["getBars"] = (
    symbolInfo,
    resolution,
    periodParams,
    onHistoryCallback,
    onErrorCallback,
  ) => {
    if (resolution !== lastResolution) {
      consecutiveEmpty = 0;
      lastResolution = resolution;
    }
    // Ensure we always fetch a meaningful chunk from the API so TradingView
    // doesn't have to paginate through gaps one tiny window at a time.
    // Overnight stocks need a larger window to clear 8pm-4am gaps in one hop.
    const minWindowMs =
      resolution === DAILY_RESOLUTION
        ? 0
        : isOvernightTradable
          ? OVERNIGHT_HISTORY_MIN_WINDOW_MS
          : HISTORY_MIN_WINDOW_MS;
    const fromMs = Math.min(
      periodParams.from * 1000,
      periodParams.to * 1000 - minWindowMs,
    );
    const fromDate = new Date(fromMs).toISOString();

    const toDate = formatChartHistoryEndDate({
      periodToMs: periodParams.to * 1000,
      resolution,
      crypto,
      isUnlimited,
    });
    const timeframe = resolutionToTimeframe(resolution);

    const reportEmptyWindow = () => {
      consecutiveEmpty++;
      // Overnight stocks have larger gaps (overnight session + weekends)
      // so they need more attempts before giving up.
      const maxEmpty = isOvernightTradable
        ? MAX_CONSECUTIVE_EMPTY_WINDOWS_OVERNIGHT
        : MAX_CONSECUTIVE_EMPTY_WINDOWS;
      if (consecutiveEmpty >= maxEmpty) {
        Sentry.captureMessage("Chart pagination exhausted empty window limit", {
          level: "warning",
          tags: {
            component: "tv-chart-getBars",
            symbol: symbolInfo.name,
            resolution,
            overnight: String(isOvernightTradable),
          },
          extra: {
            consecutiveEmpty,
            from: fromDate,
            to: toDate,
            periodFrom: new Date(periodParams.from * 1000).toISOString(),
            periodTo: new Date(periodParams.to * 1000).toISOString(),
          },
        });
        onHistoryCallback([], { noData: true });
        return;
      }
      // Skip back by the actual checked range (which includes the
      // minWindowMs extension), not TV's requested window. This avoids
      // re-checking time we already know is empty.
      const actualFromSec = fromMs / 1000;
      const checkedSec = periodParams.to - actualFromSec;
      onHistoryCallback([], {
        noData: true,
        nextTime:
          actualFromSec - Math.max(checkedSec, MIN_EMPTY_WINDOW_STEP_BACK_SEC),
      });
    };

    const reportBars = (bars: TradingViewBar[]) => {
      if (bars.length > 0) {
        consecutiveEmpty = 0;
        onHistoryCallback(bars, { noData: false });
        return;
      }
      reportEmptyWindow();
    };

    if (crypto) {
      const url = `${cryptoDataApiUrl}/bars?symbols=${symbolInfo.name}&start=${fromDate}&end=${toDate}&timeframe=${timeframe}&limit=${HISTORY_BAR_LIMIT}`;
      instance
        .get(url)
        .then(({ data }) => {
          // v2 forwards the crypto response untouched and only sorts and
          // deduplicates the stock path, which leaves the chart at the mercy
          // of the API's ordering. TradingView requires ascending, unique
          // timestamps, so the same guarantee is applied here.
          reportBars(
            deduplicateBars(
              formatBars(data?.bars?.[symbolInfo.name]).sort(
                (a, b) => a.time - b.time,
              ),
            ),
          );
        })
        .catch((err) => {
          Sentry.captureException(err);
          onErrorCallback("Failed to load chart data");
        });
      return;
    }

    const buildStockUrl = (feed?: string) => {
      let url = `${baseUrl}/stocks/${symbolInfo.name}/bars?start=${fromDate}&end=${toDate}&timeframe=${timeframe}&adjustment=split&limit=${HISTORY_BAR_LIMIT}`;
      if (feed) {
        url += `&feed=${feed}`;
      }
      return url;
    };

    const requests: Promise<AxiosResponse>[] = [instance.get(buildStockUrl())];
    if (isOvernightTradable && resolution !== DAILY_RESOLUTION) {
      requests.push(instance.get(buildStockUrl("boats")));
    }

    Promise.allSettled(requests).then((results) => {
      const fulfilled = fulfilledResponses(results);
      if (fulfilled.length === 0) {
        const firstErr = results.find(
          (result): result is PromiseRejectedResult =>
            result.status === "rejected",
        );
        if (firstErr) {
          Sentry.captureException(firstErr.reason);
        }
        onErrorCallback("Failed to load chart data");
        return;
      }
      reportBars(
        deduplicateBars(
          barsFromResponses(fulfilled).sort((a, b) => a.time - b.time),
        ),
      );
    });
  };

  const datafeed = {
    onReady,
    resolveSymbol,
    searchSymbols,
    getBars,
    subscribeBars,
    unsubscribeBars,
  } as IBasicDataFeed;

  const dispose = () => {
    pollIntervals.forEach(clearInterval);
    pollIntervals.clear();
  };

  return { datafeed, dispose };
};
